import { z } from "zod";

export const PROJECT_CONTEXT_LIMITS = { products: 12, productLength: 120, objectiveLength: 2000 } as const;

const boundedText = (max: number) => z.string().refine((value) => [...value].length <= max, "Text exceeds the saved field limit");
const canonicalText = (max: number) => boundedText(max).refine((value) => value === value.trim(), "Expected saved canonical text");

/** Private user guidance attached to the response; never a company fact or source. */
export const projectContextGuidanceSchema = z.object({
  schema_version: z.literal(1),
  scope_node_id: boundedText(36).refine((value) => value.length > 0, "Expected a scope identifier"),
  revision: z.number().int().min(0).max(2147483647),
  products: z.array(canonicalText(PROJECT_CONTEXT_LIMITS.productLength)
    .refine((value) => value.length > 0 && !/\p{C}/u.test(value), "Expected a printable product"))
    .max(PROJECT_CONTEXT_LIMITS.products)
    .refine((values) => new Set(values.map((value) => value.toLowerCase())).size === values.length, "Products must be distinct"),
  objective: canonicalText(PROJECT_CONTEXT_LIMITS.objectiveLength)
    .refine((value) => !/\p{C}/u.test(value.replace(/[\n\r\t]/g, "")), "Invalid objective text"),
  provenance: z.literal("user_guidance"),
  // The server verifies the immutable digest. This boundary validates its format;
  // guidance neither authorizes a read nor joins public-research source registries.
  context_hash: z.string().regex(/^[0-9a-f]{64}$/),
}).strict();

export type ProjectContextGuidance = z.infer<typeof projectContextGuidanceSchema>;
